/**
 * useAircraftDragInteraction - Hook for managing mouse AND touch interactions
 * with aircraft in hangar view
 *
 * Handles:
 * - Aircraft selection via click/tap
 * - Dragging aircraft (mouse / touch on already-selected aircraft)
 * - Rotation via ring interaction (mouse / touch)
 * - Two-finger twist rotation (touch only)
 * - Hover states for cursor feedback and highlighting (mouse only)
 *
 * Touch gesture model (tap-to-select-first):
 * 1. Tap unselected aircraft -> select (map controls stay enabled)
 * 2. Drag already-selected aircraft -> move (map pan disabled)
 * 3. Drag rotation ring -> rotate
 * 4. Two-finger twist on selected aircraft -> rotate
 * 5. Tap empty area -> deselect
 *
 * Based on useBuildingDragInteraction pattern with adaptations for aircraft.
 */

import { useEffect, useRef, useCallback } from 'react'
import * as THREE from 'three'
import { useAircraftEditorStore } from '@/stores/aircraftEditorStore'
import { screenToWorldPosition } from '@/utils/coordinates'
import { FEET_TO_METERS, METERS_TO_FEET } from '@/constants/units'
import { detectHangarForPosition } from '@/lib/utils/hangar-detection'
import { SceneBuilder } from '@/services/3d/SceneBuilder'
import type { PlacedAircraft3D } from '@/services/3d'
import type { Building3DConfigExtended } from '@/services/3d/BuildingLoaderService'

interface UseAircraftDragInteractionParams {
  map: mapboxgl.Map | null
  sceneObjects: {
    scene: THREE.Scene | null
    camera: THREE.Camera | null
    aircraft: THREE.Object3D[]
    groundPlane: THREE.Mesh | null
  } | null
  enabled: boolean
  /** Aircraft data for lookup by allocation ID */
  aircraftData: PlacedAircraft3D[]
  /** Buildings/areas for live detection during drag */
  buildings: Building3DConfigExtended[]
  /** Callback when aircraft position changes (for visual update) */
  onAircraftMove?: (allocationId: string, position: [number, number, number]) => void
  /** Callback when aircraft rotation changes (for visual update) */
  onAircraftRotate?: (allocationId: string, rotation: number) => void
  /** Callback to check collision for a single aircraft (for drag optimization) */
  onCollisionCheck?: (allocationId: string) => void
}

// ========================================
// CONSTANTS
// ========================================

/** Rotation ring radius in meters */
const RING_RADIUS_METERS = 15
/** Tolerance for ring hit detection (mouse) — ring visual is 14-16m, hit zone [13m, 17m] */
const RING_TOLERANCE_METERS = 1
/** Padding added to OBB half-extents for mouse hit detection (meters) */
const OBB_HIT_PADDING_METERS = 0.5
/** Minimum half-extent for OBB hit detection when dimensions are missing (meters) */
const MIN_HIT_HALF_EXTENT_METERS = 1
/** Minimum distance in pixels to start dragging — mouse */
const DRAG_THRESHOLD_PIXELS = 5

// Touch-specific — larger targets for finger imprecision (Apple HIG: 44pt min)
/** Additional hit radius padding for touch targets (meters) */
const TOUCH_HIT_RADIUS_EXTRA = 3
/** Additional ring tolerance for touch (meters) */
const TOUCH_RING_TOLERANCE_EXTRA = 3
/** Drag threshold for touch (pixels) */
const TOUCH_DRAG_THRESHOLD_PIXELS = 8
/** Cooldown to suppress synthesized mouse events after touch (ms) */
const TOUCH_MOUSE_GUARD_MS = 500

export function useAircraftDragInteraction({
  map,
  sceneObjects,
  enabled,
  aircraftData,
  buildings,
  onAircraftMove,
  onAircraftRotate,
  onCollisionCheck,
}: UseAircraftDragInteractionParams) {
  // ========================================
  // REFS — Shared drag/rotate state
  // ========================================
  const isDraggingRef = useRef(false)
  const isRotatingRingRef = useRef(false)
  const dragOffsetRef = useRef<{ x: number; z: number }>({ x: 0, z: 0 })
  const pendingDragRef = useRef(false)
  const mouseDownPosRef = useRef<{ x: number; y: number } | null>(null)
  const pendingDeselectRef = useRef(false)
  const tempVec2Ref = useRef(new THREE.Vector2())

  // Intermediate state during drag/rotate — committed to store on mouseup/touchend
  const intermediateRef = useRef<{
    allocationId: string | null
    position: [number, number, number] | null
    rotation: number | null
    detection: { areaId: string | null; unitId: string | null } | null
  }>({ allocationId: null, position: null, rotation: null, detection: null })

  const rotationRingRef = useRef<THREE.Group | null>(null)
  /** Cursor angle (degrees) at ring-drag start — used to compute delta rotation */
  const ringDragStartAngleRef = useRef<number | null>(null)
  /** Aircraft rotation (degrees) at ring-drag start */
  const ringDragStartRotationRef = useRef<number | null>(null)

  // ========================================
  // REFS — Touch-specific
  // ========================================
  /** True when this touch gesture selected the aircraft (don't start drag until next touch) */
  const touchSelectedThisTapRef = useRef(false)
  /** True when in two-finger twist rotation */
  const isTwoFingerTwistRef = useRef(false)
  /** Screen-space angle between two fingers at twist start */
  const twoFingerStartAngleRef = useRef<number | null>(null)
  /** Aircraft rotation at twist start */
  const twoFingerStartRotationRef = useRef<number | null>(null)
  /** Timestamp of last touch event — used to suppress synthesized mouse events */
  const lastTouchTimeRef = useRef(0)
  /** True when cursor-follow mode is active (aircraft follows mouse without button held) */
  const cursorFollowActiveRef = useRef(false)

  /**
   * Create or update rotation ring around selected aircraft
   */
  const updateRotationRing = useCallback((
    scene: THREE.Scene,
    selectedId: string | null
  ) => {
    // Remove existing ring group
    if (rotationRingRef.current) {
      scene.remove(rotationRingRef.current)
      rotationRingRef.current.traverse((child) => {
        if (child instanceof THREE.Mesh) {
          child.geometry.dispose()
          ;(child.material as THREE.Material).dispose()
        }
      })
      rotationRingRef.current = null
    }

    // Add ring + arrows for selected aircraft
    if (selectedId) {
      const selectedAircraft = scene.getObjectByName(selectedId)
      if (selectedAircraft) {
        const group = new THREE.Group()
        group.name = 'rotation-ring'

        // Flat ring
        const ring = new THREE.Mesh(
          new THREE.RingGeometry(
            RING_RADIUS_METERS - 1,
            RING_RADIUS_METERS + 1,
            64
          ),
          new THREE.MeshBasicMaterial({
            color: 0x3498db,
            transparent: true,
            opacity: 0.5,
            side: THREE.DoubleSide,
          })
        )
        ring.rotation.x = -Math.PI / 2
        group.add(ring)

        // 4 solid triangle arrows pointing clockwise
        const arrowShape = SceneBuilder.createArrowShape()
        const arrowGeom = new THREE.ShapeGeometry(arrowShape)
        const arrowMat = new THREE.MeshBasicMaterial({
          color: 0x3498db,
          transparent: true,
          opacity: 0.7,
          side: THREE.DoubleSide,
        })

        const arrowAngles = [0, Math.PI / 2, Math.PI, Math.PI * 3 / 2]
        for (const angle of arrowAngles) {
          const arrow = new THREE.Mesh(arrowGeom, arrowMat)
          SceneBuilder.orientArrow(arrow, angle)
          arrow.position.set(
            RING_RADIUS_METERS * Math.sin(angle),
            0.05,
            RING_RADIUS_METERS * Math.cos(angle)
          )
          group.add(arrow)
        }

        group.position.copy(selectedAircraft.position)
        group.position.y = 0.2
        scene.add(group)
        rotationRingRef.current = group
      }
    }
  }, [])

  // RAF-batched repaint to coalesce per-event triggerRepaint calls
  const repaintScheduledRef = useRef(false)
  const mapRef = useRef<mapboxgl.Map | null>(null)
  if (map) mapRef.current = map

  const scheduleRepaint = useCallback(() => {
    if (repaintScheduledRef.current) return
    repaintScheduledRef.current = true
    requestAnimationFrame(() => {
      repaintScheduledRef.current = false
      mapRef.current?.triggerRepaint()
    })
  }, [])

  useEffect(() => {
    if (!map || !sceneObjects || !enabled) return

    const canvas = map.getCanvas()

    // ========================================
    // HELPER FUNCTIONS
    // ========================================

    /**
     * Get world position from NDC using Mapbox's unproject method
     */
    const getWorldPosition = (mouse: THREE.Vector2): THREE.Vector3 | null => {
      const { mapCenter } = useAircraftEditorStore.getState()
      if (!mapCenter) {
        const center = map.getCenter()
        return screenToWorldPosition(mouse, map, [center.lng, center.lat])
      }
      return screenToWorldPosition(mouse, map, [mapCenter.lng, mapCenter.lat])
    }

    /**
     * Convert screen pixel coordinates to world position
     */
    const getWorldFromScreen = (screenX: number, screenY: number): THREE.Vector3 | null => {
      const mouse = tempVec2Ref.current.set(
        (screenX / canvas.clientWidth) * 2 - 1,
        -(screenY / canvas.clientHeight) * 2 + 1
      )
      return getWorldPosition(mouse)
    }

    /**
     * Check if a world-space point is inside an oriented bounding box
     * centered at aircraftPos, rotated by headingDeg, with given half-extents.
     */
    const isPointInOBB = (
      point: THREE.Vector3,
      center: THREE.Vector3,
      headingDeg: number,
      halfX: number,
      halfZ: number,
    ): boolean => {
      const dx = point.x - center.x
      const dz = point.z - center.z
      const rad = (headingDeg * Math.PI) / 180
      const cos = Math.cos(rad)
      const sin = Math.sin(rad)
      // Inverse-rotate into aircraft-local frame
      const localX = dx * cos + dz * sin
      const localZ = -dx * sin + dz * cos
      return Math.abs(localX) <= halfX && Math.abs(localZ) <= halfZ
    }

    /**
     * Find aircraft at screen position using OBB hit detection based on aircraft dimensions
     * @param extraPadding Additional padding in meters added to OBB half-extents (used for touch targets)
     */
    const getAircraftAtPosition = (screenX: number, screenY: number, extraPadding = 0): THREE.Object3D | null => {
      const worldPos = getWorldFromScreen(screenX, screenY)
      if (!worldPos || !sceneObjects.scene) return null

      for (const data of aircraftData) {
        const aircraft = sceneObjects.scene.getObjectByName(data.id)
        if (!aircraft) continue

        const dims = data.physicalDimensions
        const wingspanM = dims ? dims.wingspan * FEET_TO_METERS : MIN_HIT_HALF_EXTENT_METERS * 2
        const lengthM = dims ? dims.length * FEET_TO_METERS : MIN_HIT_HALF_EXTENT_METERS * 2

        const padding = OBB_HIT_PADDING_METERS + extraPadding
        const halfX = Math.max(wingspanM / 2, MIN_HIT_HALF_EXTENT_METERS) + padding
        const halfZ = Math.max(lengthM / 2, MIN_HIT_HALF_EXTENT_METERS) + padding

        if (isPointInOBB(worldPos, aircraft.position, data.rotation, halfX, halfZ)) {
          return aircraft
        }
      }

      return null
    }

    /**
     * Check if screen position is over the rotation ring
     * @param extraTolerance Additional tolerance in meters (used for touch targets)
     */
    const isOverRotationRing = (screenX: number, screenY: number, extraTolerance = 0): boolean => {
      const { selectedAllocationId } = useAircraftEditorStore.getState()
      if (!selectedAllocationId) return false

      const selectedAircraft = sceneObjects.scene?.getObjectByName(selectedAllocationId)
      if (!selectedAircraft) return false

      const worldPos = getWorldFromScreen(screenX, screenY)
      if (!worldPos) return false

      const dx = worldPos.x - selectedAircraft.position.x
      const dz = worldPos.z - selectedAircraft.position.z
      const dist = Math.sqrt(dx * dx + dz * dz)

      // Asymmetric tolerance: tight inward, generous outward (touch registers above finger contact)
      const innerTolerance = RING_TOLERANCE_METERS + extraTolerance * 0.3
      const outerTolerance = RING_TOLERANCE_METERS + extraTolerance
      return dist >= RING_RADIUS_METERS - innerTolerance && dist <= RING_RADIUS_METERS + outerTolerance
    }

    /**
     * Get aircraft data by allocation ID
     */
    const getAircraftData = (allocationId: string): PlacedAircraft3D | undefined => {
      return aircraftData.find(a => a.id === allocationId)
    }

    // ========================================
    // SHARED INTERACTION LOGIC
    // Called by both mouse and touch handlers
    // ========================================

    /**
     * Apply rotation visually and buffer to intermediate state
     */
    const applyRotation = (selectedAllocationId: string, degrees: number) => {
      intermediateRef.current.allocationId = selectedAllocationId
      intermediateRef.current.rotation = degrees

      const selectedAircraft = sceneObjects.scene?.getObjectByName(selectedAllocationId)
      if (!selectedAircraft) return

      const data = getAircraftData(selectedAllocationId)
      if (data) {
        const baseRotation = data.aircraftType.modelSettings.rotation ?? [0, 0, 0]
        const headingAxis = data.aircraftType.modelSettings.headingAxis ?? 'y'
        const headingRad = (degrees * Math.PI) / 180
        const baseRotX = (baseRotation[0] * Math.PI) / 180
        const baseRotY = (baseRotation[1] * Math.PI) / 180
        const baseRotZ = (baseRotation[2] * Math.PI) / 180

        if (headingAxis === 'y') {
          selectedAircraft.rotation.set(baseRotX, baseRotY + headingRad, baseRotZ)
        } else if (headingAxis === 'z') {
          selectedAircraft.rotation.set(baseRotX, baseRotY, baseRotZ + headingRad)
        } else {
          selectedAircraft.rotation.set(baseRotX + headingRad, baseRotY, baseRotZ)
        }
      }

      onAircraftRotate?.(selectedAllocationId, degrees)
      onCollisionCheck?.(selectedAllocationId)
      scheduleRepaint()
    }

    /**
     * Calculate rotation degrees from world position relative to aircraft center
     */
    const calcRotationFromWorldPos = (worldPos: THREE.Vector3, aircraftPos: THREE.Vector3): number => {
      const dx = worldPos.x - aircraftPos.x
      const dz = worldPos.z - aircraftPos.z
      const angle = Math.atan2(dx, dz)
      const degrees = (angle * 180) / Math.PI
      return ((degrees % 360) + 360) % 360
    }

    /**
     * Apply drag visually and buffer to intermediate state
     */
    const applyDrag = (selectedAllocationId: string, worldPos: THREE.Vector3) => {
      const selectedAircraft = sceneObjects.scene?.getObjectByName(selectedAllocationId)
      if (!selectedAircraft) return

      const newX = worldPos.x + dragOffsetRef.current.x
      const newZ = worldPos.z + dragOffsetRef.current.z

      selectedAircraft.position.x = newX
      selectedAircraft.position.z = newZ

      if (rotationRingRef.current) {
        rotationRingRef.current.position.x = newX
        rotationRingRef.current.position.z = newZ
      }

      const positionFeet: [number, number, number] = [
        newX * METERS_TO_FEET,
        selectedAircraft.position.y * METERS_TO_FEET,
        newZ * METERS_TO_FEET,
      ]

      const detection = detectHangarForPosition(positionFeet, buildings)

      intermediateRef.current.allocationId = selectedAllocationId
      intermediateRef.current.position = positionFeet
      intermediateRef.current.detection = detection

      onAircraftMove?.(selectedAllocationId, positionFeet)
      onCollisionCheck?.(selectedAllocationId)
      scheduleRepaint()
    }

    /**
     * Commit buffered intermediate state to store
     */
    const commitBufferedState = () => {
      const { allocationId, position, rotation, detection } = intermediateRef.current
      if (allocationId) {
        const { updatePosition, updateRotation, updateDetection } = useAircraftEditorStore.getState()
        if (position) updatePosition(allocationId, position)
        if (rotation !== null) updateRotation(allocationId, rotation)
        if (detection) updateDetection(allocationId, detection)
        intermediateRef.current = { allocationId: null, position: null, rotation: null, detection: null }
      }
    }

    /**
     * Re-enable all map controls (pan, rotate, touchZoomRotate)
     */
    const enableMapControls = () => {
      map.dragPan.enable()
      map.dragRotate.enable()
      map.touchZoomRotate?.enable()
    }

    /**
     * Disable map controls that conflict with aircraft interaction
     */
    const disableMapControls = () => {
      map.dragPan.disable()
      map.dragRotate.disable()
      map.touchZoomRotate?.disable()
    }

    // ========================================
    // MOUSE EVENT HANDLERS
    // ========================================

    const handleMouseDown = (e: mapboxgl.MapMouseEvent) => {
      // Suppress synthesized mouse events from touch
      if (Date.now() - lastTouchTimeRef.current < TOUCH_MOUSE_GUARD_MS) return

      // Only handle left mouse button
      if (e.originalEvent.button !== 0) return

      // --- Cursor-follow: click to place ---
      if (cursorFollowActiveRef.current) {
        const { cursorFollowTargetId } = useAircraftEditorStore.getState()
        if (cursorFollowTargetId) {
          commitBufferedState()
          // Exit cursor-follow but keep MoveBar visible (isDragging stays true in store)
          cursorFollowActiveRef.current = false
          isDraggingRef.current = false
          useAircraftEditorStore.setState({ cursorFollowTargetId: null })
          enableMapControls()
          canvas.style.cursor = 'default'
          console.log('Cursor-follow: placed aircraft — MoveBar stays for Save/Revert')
        }
        e.preventDefault()
        return
      }

      const {
        selectedAllocationId,
        selectAllocation,
                setRotating,
        startEditing,
        isDragDropEnabled,
        cursorFollowTargetId,
      } = useAircraftEditorStore.getState()

      // Check rotation ring first (if aircraft already selected and ring is visible)
      if (selectedAllocationId && rotationRingRef.current && isOverRotationRing(e.point.x, e.point.y)) {
        isRotatingRingRef.current = true
        // Capture initial cursor angle and aircraft rotation for delta-based rotation
        const selectedAircraft = sceneObjects.scene?.getObjectByName(selectedAllocationId)
        const worldPos = getWorldFromScreen(e.point.x, e.point.y)
        if (worldPos && selectedAircraft) {
          ringDragStartAngleRef.current = calcRotationFromWorldPos(worldPos, selectedAircraft.position)
          const data = getAircraftData(selectedAllocationId)
          ringDragStartRotationRef.current = intermediateRef.current.rotation ?? data?.rotation ?? 0
        }
        e.preventDefault()
        map.dragPan.disable()
        map.dragRotate.disable()
        setRotating(true)
        canvas.style.cursor = 'grabbing'
        console.log('Aircraft rotation started')
        return
      }

      // Check if clicking on an aircraft
      const hitAircraft = getAircraftAtPosition(e.point.x, e.point.y)

      if (hitAircraft) {
        const allocationId = hitAircraft.name
        const data = getAircraftData(allocationId)

        if (data) {
          selectAllocation(allocationId)

          const initialDetection = detectHangarForPosition(data.position, buildings)
          startEditing(
            allocationId,
            data.aircraftId,
            data.tailNumber,
            data.position,
            data.rotation,
            initialDetection
          )

          // Gate: allow drag if D&D enabled, cursor-follow targets this aircraft, or position is dirty (post-placement fine-tune)
          const canDrag = isDragDropEnabled
            || cursorFollowTargetId === allocationId
            || useAircraftEditorStore.getState().isDirty(allocationId)

          if (canDrag) {
            // Calculate drag offset
            const worldPos = getWorldFromScreen(e.point.x, e.point.y)
            if (worldPos) {
              dragOffsetRef.current = {
                x: hitAircraft.position.x - worldPos.x,
                z: hitAircraft.position.z - worldPos.z,
              }
            }

            pendingDragRef.current = true
            mouseDownPosRef.current = { x: e.point.x, y: e.point.y }
            e.preventDefault()
            map.dragPan.disable()
            map.dragRotate.disable()
            canvas.style.cursor = 'pointer'
          } else {
            // Selection only — no drag setup
            mouseDownPosRef.current = { x: e.point.x, y: e.point.y }
          }

          // Only show rotation ring if interaction is allowed
          const shouldShowRing = isDragDropEnabled
            || cursorFollowTargetId === allocationId
            || useAircraftEditorStore.getState().isDirty(allocationId)
          if (sceneObjects.scene) {
            updateRotationRing(sceneObjects.scene, shouldShowRing ? allocationId : null)
          }

          console.log('Aircraft selected:', data.tailNumber)
        }
        return
      }

      // Click on empty space — defer deselect to mouseup (so map drag doesn't close card)
      if (selectedAllocationId) {
        pendingDeselectRef.current = true
        mouseDownPosRef.current = { x: e.point.x, y: e.point.y }
      }
    }

    const handleMouseMove = (e: mapboxgl.MapMouseEvent) => {
      if (Date.now() - lastTouchTimeRef.current < TOUCH_MOUSE_GUARD_MS) return

      // --- Cursor-follow: aircraft follows mouse without button held ---
      if (cursorFollowActiveRef.current) {
        const { cursorFollowTargetId } = useAircraftEditorStore.getState()
        if (cursorFollowTargetId) {
          const worldPos = getWorldFromScreen(e.point.x, e.point.y)
          if (worldPos) {
            applyDrag(cursorFollowTargetId, worldPos)
          }
          canvas.style.cursor = 'move'
        }
        return
      }

      // Cancel pending deselect if mouse moved enough (user is panning)
      if (pendingDeselectRef.current && mouseDownPosRef.current) {
        const ddx = e.point.x - mouseDownPosRef.current.x
        const ddy = e.point.y - mouseDownPosRef.current.y
        if (Math.sqrt(ddx * ddx + ddy * ddy) >= DRAG_THRESHOLD_PIXELS) {
          pendingDeselectRef.current = false
        }
      }

      const {
        selectedAllocationId,
        setHovered,
        setDragging,
      } = useAircraftEditorStore.getState()

      // Handle rotation (delta-based: rotation = startRotation + cursorDelta)
      if (isRotatingRingRef.current && selectedAllocationId) {
        const worldPos = getWorldFromScreen(e.point.x, e.point.y)
        const selectedAircraft = sceneObjects.scene?.getObjectByName(selectedAllocationId)

        if (worldPos && selectedAircraft && ringDragStartAngleRef.current !== null) {
          const currentAngle = calcRotationFromWorldPos(worldPos, selectedAircraft.position)
          const delta = currentAngle - ringDragStartAngleRef.current
          let newRotation = (ringDragStartRotationRef.current ?? 0) + delta
          newRotation = ((newRotation % 360) + 360) % 360
          applyRotation(selectedAllocationId, newRotation)
        }

        canvas.style.cursor = 'grabbing'
        return
      }

      // Check if pending drag should become actual drag (threshold check)
      if (pendingDragRef.current && !isDraggingRef.current && mouseDownPosRef.current) {
        const dx = e.point.x - mouseDownPosRef.current.x
        const dy = e.point.y - mouseDownPosRef.current.y
        const distance = Math.sqrt(dx * dx + dy * dy)

        if (distance >= DRAG_THRESHOLD_PIXELS) {
          isDraggingRef.current = true
          setDragging(true)
          canvas.style.cursor = 'move'
          console.log('Aircraft drag started (threshold exceeded)')
        } else {
          return
        }
      }

      // Handle dragging
      if (isDraggingRef.current && selectedAllocationId) {
        const worldPos = getWorldFromScreen(e.point.x, e.point.y)
        if (worldPos) {
          applyDrag(selectedAllocationId, worldPos)
        }
        canvas.style.cursor = 'move'
        return
      }

      // Handle hover (not dragging) — mouse only
      if (!isDraggingRef.current && !isRotatingRingRef.current) {
        if (selectedAllocationId && rotationRingRef.current && isOverRotationRing(e.point.x, e.point.y)) {
          canvas.style.cursor = 'grab'
          return
        }

        const hitAircraft = getAircraftAtPosition(e.point.x, e.point.y)
        if (hitAircraft) {
          setHovered(hitAircraft.name)
          canvas.style.cursor = 'pointer'
        } else {
          setHovered(null)
          canvas.style.cursor = 'default'
        }
      }
    }

    const handleMouseUp = (e: mapboxgl.MapMouseEvent) => {
      if (Date.now() - lastTouchTimeRef.current < TOUCH_MOUSE_GUARD_MS) return

      // Deselect only if user truly clicked (not dragged) on empty space
      if (pendingDeselectRef.current) {
        pendingDeselectRef.current = false
        const down = mouseDownPosRef.current
        const wasDrag = down &&
          Math.sqrt((e.point.x - down.x) ** 2 + (e.point.y - down.y) ** 2) >= DRAG_THRESHOLD_PIXELS
        if (!wasDrag) {
          const { selectAllocation } = useAircraftEditorStore.getState()
          selectAllocation(null)
          if (sceneObjects.scene) {
            updateRotationRing(sceneObjects.scene, null)
          }
        }
      }

      const wasInteracting = isDraggingRef.current || isRotatingRingRef.current || pendingDragRef.current

      commitBufferedState()

      isDraggingRef.current = false
      isRotatingRingRef.current = false
      pendingDragRef.current = false
      mouseDownPosRef.current = null
      ringDragStartAngleRef.current = null
      ringDragStartRotationRef.current = null

      if (wasInteracting) {
        map.dragPan.enable()
        map.dragRotate.enable()

        const { setDragging, setRotating } = useAircraftEditorStore.getState()
        setDragging(false)
        setRotating(false)

        canvas.style.cursor = 'default'
        console.log('Aircraft interaction ended')
      }
    }

    const handleMouseOut = () => {
      if (Date.now() - lastTouchTimeRef.current < TOUCH_MOUSE_GUARD_MS) return

      pendingDeselectRef.current = false

      const wasInteracting = isDraggingRef.current || isRotatingRingRef.current || pendingDragRef.current

      commitBufferedState()

      isDraggingRef.current = false
      isRotatingRingRef.current = false
      pendingDragRef.current = false
      mouseDownPosRef.current = null
      ringDragStartAngleRef.current = null
      ringDragStartRotationRef.current = null

      if (wasInteracting) {
        map.dragPan.enable()
        map.dragRotate.enable()

        const { setDragging, setRotating, setHovered } =
          useAircraftEditorStore.getState()
        setDragging(false)
        setRotating(false)
        setHovered(null)

        console.log('Mouse left map - aircraft interaction cancelled')
      }

      canvas.style.cursor = 'default'
    }

    // ========================================
    // TOUCH EVENT HANDLERS
    // ========================================

    const handleTouchStart = (e: mapboxgl.MapTouchEvent) => {
      lastTouchTimeRef.current = Date.now()
      const touches = e.originalEvent.touches

      // --- Multi-touch (2+ fingers) ---
      if (touches.length >= 2) {
        const { selectedAllocationId } = useAircraftEditorStore.getState()

        // Cancel any in-progress single-finger drag
        if (isDraggingRef.current || pendingDragRef.current) {
          isDraggingRef.current = false
          pendingDragRef.current = false
          useAircraftEditorStore.getState().setDragging(false)
        }

        // Start two-finger twist if an aircraft is selected AND interaction is allowed
        if (selectedAllocationId && e.points.length >= 2) {
          const { isDragDropEnabled, cursorFollowTargetId } = useAircraftEditorStore.getState()
          const canRotate = isDragDropEnabled
            || cursorFollowTargetId === selectedAllocationId
            || useAircraftEditorStore.getState().isDirty(selectedAllocationId)
          if (!canRotate) return

          isTwoFingerTwistRef.current = true
          twoFingerStartAngleRef.current = Math.atan2(
            e.points[1].y - e.points[0].y,
            e.points[1].x - e.points[0].x
          )
          // Use intermediate rotation if available (from a prior drag), else aircraft data
          if (intermediateRef.current.rotation !== null) {
            twoFingerStartRotationRef.current = intermediateRef.current.rotation
          } else {
            const data = getAircraftData(selectedAllocationId)
            twoFingerStartRotationRef.current = data?.rotation ?? 0
          }

          e.preventDefault()
          disableMapControls()
          useAircraftEditorStore.getState().setRotating(true)
          console.log('Two-finger twist started')
        }
        return
      }

      // --- Single touch ---
      const {
        selectedAllocationId,
        selectAllocation,
        startEditing,
      } = useAircraftEditorStore.getState()

      // Check rotation ring (with wider touch tolerance, only if ring is visible)
      if (selectedAllocationId && rotationRingRef.current && isOverRotationRing(e.point.x, e.point.y, TOUCH_RING_TOLERANCE_EXTRA)) {
        isRotatingRingRef.current = true
        touchSelectedThisTapRef.current = false
        // Capture initial cursor angle and aircraft rotation for delta-based rotation
        const selectedAircraft = sceneObjects.scene?.getObjectByName(selectedAllocationId)
        const worldPos = getWorldFromScreen(e.point.x, e.point.y)
        if (worldPos && selectedAircraft) {
          ringDragStartAngleRef.current = calcRotationFromWorldPos(worldPos, selectedAircraft.position)
          const data = getAircraftData(selectedAllocationId)
          ringDragStartRotationRef.current = intermediateRef.current.rotation ?? data?.rotation ?? 0
        }
        e.preventDefault()
        disableMapControls()
        useAircraftEditorStore.getState().setRotating(true)
        console.log('Touch: rotation ring started')
        return
      }

      // Check aircraft hit (with larger touch target)
      const hitAircraft = getAircraftAtPosition(e.point.x, e.point.y, TOUCH_HIT_RADIUS_EXTRA)

      if (hitAircraft) {
        const allocationId = hitAircraft.name
        const data = getAircraftData(allocationId)
        if (!data) return

        const wasAlreadySelected = selectedAllocationId === allocationId

        if (!wasAlreadySelected) {
          // TAP-TO-SELECT: First touch on unselected aircraft
          // Select it but don't start drag — map controls stay enabled
          selectAllocation(allocationId)
          touchSelectedThisTapRef.current = true

          const initialDetection = detectHangarForPosition(data.position, buildings)
          startEditing(
            allocationId,
            data.aircraftId,
            data.tailNumber,
            data.position,
            data.rotation,
            initialDetection
          )

          // Only show rotation ring if interaction is allowed
          const { isDragDropEnabled: touchDragEnabled, cursorFollowTargetId: touchCursorTarget } = useAircraftEditorStore.getState()
          const touchShouldShowRing = touchDragEnabled
            || touchCursorTarget === allocationId
            || useAircraftEditorStore.getState().isDirty(allocationId)
          if (sceneObjects.scene) {
            updateRotationRing(sceneObjects.scene, touchShouldShowRing ? allocationId : null)
          }

          mouseDownPosRef.current = { x: e.point.x, y: e.point.y }
          console.log('Touch: aircraft selected (tap-to-select):', data.tailNumber)
          // Don't preventDefault — map controls stay enabled for pan/zoom
          return
        }

        // DRAG: Touch on already-selected aircraft — start pending drag (if allowed)
        touchSelectedThisTapRef.current = false

        const initialDetection = detectHangarForPosition(data.position, buildings)
        startEditing(
          allocationId,
          data.aircraftId,
          data.tailNumber,
          data.position,
          data.rotation,
          initialDetection
        )

        // Gate: allow drag if D&D enabled, cursor-follow targets this aircraft, or position is dirty (post-placement fine-tune)
        const { isDragDropEnabled, cursorFollowTargetId } = useAircraftEditorStore.getState()
        const canDrag = isDragDropEnabled
          || cursorFollowTargetId === allocationId
          || useAircraftEditorStore.getState().isDirty(allocationId)

        if (canDrag) {
          // Calculate drag offset
          const worldPos = getWorldFromScreen(e.point.x, e.point.y)
          if (worldPos) {
            dragOffsetRef.current = {
              x: hitAircraft.position.x - worldPos.x,
              z: hitAircraft.position.z - worldPos.z,
            }
          }

          pendingDragRef.current = true
          mouseDownPosRef.current = { x: e.point.x, y: e.point.y }
          e.preventDefault()
          disableMapControls()
          console.log('Touch: pending drag on selected aircraft:', data.tailNumber)
        } else {
          console.log('Touch: drag locked for', data.tailNumber)
        }
        return
      }

      // Touch on empty space — pending deselect
      if (selectedAllocationId) {
        pendingDeselectRef.current = true
        mouseDownPosRef.current = { x: e.point.x, y: e.point.y }
      }
    }

    const handleTouchMove = (e: mapboxgl.MapTouchEvent) => {
      lastTouchTimeRef.current = Date.now()

      // --- Two-finger twist ---
      if (isTwoFingerTwistRef.current && e.points.length >= 2) {
        const { selectedAllocationId } = useAircraftEditorStore.getState()
        if (!selectedAllocationId) return

        const currentAngle = Math.atan2(
          e.points[1].y - e.points[0].y,
          e.points[1].x - e.points[0].x
        )
        const deltaAngle = currentAngle - (twoFingerStartAngleRef.current ?? 0)
        const deltaDegrees = (deltaAngle * 180) / Math.PI
        // Negate delta: clockwise finger twist = clockwise aircraft rotation
        let newRotation = (twoFingerStartRotationRef.current ?? 0) - deltaDegrees
        newRotation = ((newRotation % 360) + 360) % 360

        applyRotation(selectedAllocationId, newRotation)
        return
      }

      // Cancel pending deselect if finger moved enough
      if (pendingDeselectRef.current && mouseDownPosRef.current) {
        const ddx = e.point.x - mouseDownPosRef.current.x
        const ddy = e.point.y - mouseDownPosRef.current.y
        if (Math.sqrt(ddx * ddx + ddy * ddy) >= TOUCH_DRAG_THRESHOLD_PIXELS) {
          pendingDeselectRef.current = false
        }
      }

      // Don't drag if this touch just selected the aircraft
      if (touchSelectedThisTapRef.current) return

      const { selectedAllocationId } = useAircraftEditorStore.getState()

      // Ring rotation (single finger, delta-based)
      if (isRotatingRingRef.current && selectedAllocationId) {
        const worldPos = getWorldFromScreen(e.point.x, e.point.y)
        const selectedAircraft = sceneObjects.scene?.getObjectByName(selectedAllocationId)
        if (worldPos && selectedAircraft && ringDragStartAngleRef.current !== null) {
          const currentAngle = calcRotationFromWorldPos(worldPos, selectedAircraft.position)
          const delta = currentAngle - ringDragStartAngleRef.current
          let newRotation = (ringDragStartRotationRef.current ?? 0) + delta
          newRotation = ((newRotation % 360) + 360) % 360
          applyRotation(selectedAllocationId, newRotation)
        }
        return
      }

      // Pending drag -> actual drag (threshold check)
      if (pendingDragRef.current && !isDraggingRef.current && mouseDownPosRef.current) {
        const dx = e.point.x - mouseDownPosRef.current.x
        const dy = e.point.y - mouseDownPosRef.current.y
        if (Math.sqrt(dx * dx + dy * dy) >= TOUCH_DRAG_THRESHOLD_PIXELS) {
          isDraggingRef.current = true
          useAircraftEditorStore.getState().setDragging(true)
          console.log('Touch: aircraft drag started')
        } else {
          return
        }
      }

      // Drag
      if (isDraggingRef.current && selectedAllocationId) {
        const worldPos = getWorldFromScreen(e.point.x, e.point.y)
        if (worldPos) {
          applyDrag(selectedAllocationId, worldPos)
        }
      }
    }

    const handleTouchEnd = (e: mapboxgl.MapTouchEvent) => {
      lastTouchTimeRef.current = Date.now()

      // Deselect on tap on empty space
      if (pendingDeselectRef.current) {
        pendingDeselectRef.current = false
        const down = mouseDownPosRef.current
        const wasDrag = down &&
          Math.sqrt((e.point.x - down.x) ** 2 + (e.point.y - down.y) ** 2) >= TOUCH_DRAG_THRESHOLD_PIXELS
        if (!wasDrag) {
          const { selectAllocation } = useAircraftEditorStore.getState()
          selectAllocation(null)
          if (sceneObjects.scene) {
            updateRotationRing(sceneObjects.scene, null)
          }
        }
      }

      const wasInteracting = isDraggingRef.current || isRotatingRingRef.current ||
        pendingDragRef.current || isTwoFingerTwistRef.current

      commitBufferedState()

      // Reset all interaction state
      isDraggingRef.current = false
      isRotatingRingRef.current = false
      pendingDragRef.current = false
      mouseDownPosRef.current = null
      touchSelectedThisTapRef.current = false
      isTwoFingerTwistRef.current = false
      twoFingerStartAngleRef.current = null
      twoFingerStartRotationRef.current = null
      ringDragStartAngleRef.current = null
      ringDragStartRotationRef.current = null

      if (wasInteracting) {
        enableMapControls()
        const { setDragging, setRotating } = useAircraftEditorStore.getState()
        setDragging(false)
        setRotating(false)
        console.log('Touch: interaction ended')
      }
    }

    const handleTouchCancel = () => {
      lastTouchTimeRef.current = Date.now()
      pendingDeselectRef.current = false

      commitBufferedState()

      // Reset all state
      isDraggingRef.current = false
      isRotatingRingRef.current = false
      pendingDragRef.current = false
      mouseDownPosRef.current = null
      touchSelectedThisTapRef.current = false
      isTwoFingerTwistRef.current = false
      twoFingerStartAngleRef.current = null
      twoFingerStartRotationRef.current = null
      ringDragStartAngleRef.current = null
      ringDragStartRotationRef.current = null

      enableMapControls()

      const { setDragging, setRotating, setHovered } = useAircraftEditorStore.getState()
      setDragging(false)
      setRotating(false)
      setHovered(null)
      console.log('Touch cancelled — interaction reset')
    }

    // ========================================
    // ATTACH EVENT LISTENERS
    // ========================================
    console.log('Attaching aircraft interaction event listeners (mouse + touch)')
    map.on('mousedown', handleMouseDown)
    map.on('mousemove', handleMouseMove)
    map.on('mouseup', handleMouseUp)
    map.on('mouseout', handleMouseOut)
    map.on('touchstart', handleTouchStart)
    map.on('touchmove', handleTouchMove)
    map.on('touchend', handleTouchEnd)
    map.on('touchcancel', handleTouchCancel)

    // --- Cursor-follow activation (inside main useEffect) ---
    const unsubCursorFollow = useAircraftEditorStore.subscribe(
      (state) => state.cursorFollowTargetId,
      (targetId) => {
        if (targetId) {
          const data = aircraftData.find(a => a.id === targetId)
          if (!data) return

          const { selectAllocation, startEditing } = useAircraftEditorStore.getState()
          selectAllocation(targetId)

          const detection = detectHangarForPosition(data.position, buildings)
          startEditing(targetId, data.aircraftId, data.tailNumber, data.position, data.rotation, detection)

          dragOffsetRef.current = { x: 0, z: 0 }
          cursorFollowActiveRef.current = true
          isDraggingRef.current = true
          disableMapControls()
          canvas.style.cursor = 'move'

          if (sceneObjects.scene) {
            updateRotationRing(sceneObjects.scene, targetId)
          }

          console.log('Cursor-follow started for:', data.tailNumber)
        } else {
          cursorFollowActiveRef.current = false
          isDraggingRef.current = false
          enableMapControls()
          canvas.style.cursor = 'default'
        }
      }
    )

    // --- Escape key for cursor-follow (inside main useEffect) ---
    const handleEscapeKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' || !cursorFollowActiveRef.current) return

      const { cursorFollowTargetId, cancelCursorFollow, revertAllocation } =
        useAircraftEditorStore.getState()
      if (!cursorFollowTargetId) return

      const reverted = revertAllocation(cursorFollowTargetId)
      if (reverted && sceneObjects.scene) {
        const aircraft = sceneObjects.scene.getObjectByName(cursorFollowTargetId)
        if (aircraft) {
          aircraft.position.set(
            reverted.originalPosition[0] * FEET_TO_METERS,
            reverted.originalPosition[1] * FEET_TO_METERS,
            reverted.originalPosition[2] * FEET_TO_METERS
          )
          const ring = sceneObjects.scene.getObjectByName('rotation-ring')
          if (ring) ring.position.set(aircraft.position.x, 0.2, aircraft.position.z)
          map.triggerRepaint()
        }
      }

      cancelCursorFollow()
      console.log('Cursor-follow cancelled via Escape')
    }
    document.addEventListener('keydown', handleEscapeKey)

    // Cleanup
    return () => {
      console.log('Removing aircraft interaction event listeners')

      unsubCursorFollow()
      document.removeEventListener('keydown', handleEscapeKey)
      cursorFollowActiveRef.current = false

      pendingDeselectRef.current = false
      if (isDraggingRef.current || isRotatingRingRef.current || pendingDragRef.current || isTwoFingerTwistRef.current) {
        isDraggingRef.current = false
        isRotatingRingRef.current = false
        pendingDragRef.current = false
        mouseDownPosRef.current = null
        touchSelectedThisTapRef.current = false
        isTwoFingerTwistRef.current = false
        twoFingerStartAngleRef.current = null
        twoFingerStartRotationRef.current = null
        enableMapControls()
      }

      // Remove rotation ring group
      if (rotationRingRef.current && sceneObjects.scene) {
        sceneObjects.scene.remove(rotationRingRef.current)
        rotationRingRef.current.traverse((child) => {
          if (child instanceof THREE.Mesh) {
            child.geometry.dispose()
            ;(child.material as THREE.Material).dispose()
          }
        })
        rotationRingRef.current = null
      }

      map.off('mousedown', handleMouseDown)
      map.off('mousemove', handleMouseMove)
      map.off('mouseup', handleMouseUp)
      map.off('mouseout', handleMouseOut)
      map.off('touchstart', handleTouchStart)
      map.off('touchmove', handleTouchMove)
      map.off('touchend', handleTouchEnd)
      map.off('touchcancel', handleTouchCancel)
    }
  }, [map, enabled, aircraftData, buildings, onAircraftMove, onAircraftRotate, onCollisionCheck, updateRotationRing, scheduleRepaint])

  // Update rotation ring when selection changes (from outside) — gated on interaction mode
  useEffect(() => {
    if (!sceneObjects?.scene) return

    const unsubSelection = useAircraftEditorStore.subscribe(
      (state) => state.selectedAllocationId,
      (selectedId) => {
        if (selectedId) {
          const { isDragDropEnabled, cursorFollowTargetId, isDirty } = useAircraftEditorStore.getState()
          const show = isDragDropEnabled || cursorFollowTargetId === selectedId || isDirty(selectedId)
          updateRotationRing(sceneObjects.scene!, show ? selectedId : null)
        } else {
          updateRotationRing(sceneObjects.scene!, null)
        }
      }
    )

    // Show/hide rotation ring when D&D toggle changes while aircraft is selected
    const unsubDragDrop = useAircraftEditorStore.subscribe(
      (state) => state.isDragDropEnabled,
      (enabled) => {
        const { selectedAllocationId, cursorFollowTargetId, isDirty } = useAircraftEditorStore.getState()
        if (!sceneObjects.scene || !selectedAllocationId) return
        const show = enabled || cursorFollowTargetId === selectedAllocationId || isDirty(selectedAllocationId)
        updateRotationRing(sceneObjects.scene, show ? selectedAllocationId : null)
      }
    )

    return () => {
      unsubSelection()
      unsubDragDrop()
    }
  }, [sceneObjects, updateRotationRing])
}
